import React, { FC, useEffect, useState } from 'react';
import { PageTitle } from '../../layout/core';
import { useTranslation } from 'react-i18next';
import { useMerchants } from '../../store/merchnts/merchants-context';
import { SearchInputSmall } from '../../components/common/SearchInputSmall';
import { ButtonComponent } from '../../components/ButtonComponent';
import { IModalWindow, ModalWindow } from '../../components/modal/ModalWindow';
import { IAccount } from '../../store/merchnts/merchant-interfaces';
import { TableComponent } from '../../components/TableComponent';
import { AccountTableRow } from '../../components/accounts/AccountTableRow';
import { AddNewAccount } from '../../components/accounts/AddNewAccount';
import { checkRoles, CLAIMS } from '../../utils/roles-helper';
import { fetchAccountList } from '../../store/merchnts/actions/account';
import { IAuthState } from '../../redux/AuthRedux';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from '../../../setup';

export const ACCOUNT_COLUMNS = [
	{
		key: 'name',
		title: 'Name',
		additionalColumnClassName: 'col-2',
	},
	{
		key: 'applications',
		title: 'Amount of applicants',
		additionalColumnClassName: 'col-2',
	},
	{
		key: 'merchants',
		title: 'Amount of merchants',
		additionalColumnClassName: 'col-8',
	},
	{
		key: 'actions',
		title: 'PARTNERS.ACTIONS',
		additionalColumnClassName: 'w-50px',
	}
];

export const Accounts: FC = () => {
	const { t } = useTranslation();
	const authUser: IAuthState = useSelector<RootState>(({ auth }) => auth, shallowEqual);

	const { merchantState: { accountList, isAccountListLoading }, dispatch } = useMerchants();

	const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
	const [searchValue, setSearchValue] = useState<string>('');
	const [modalState, setModalState] = useState<IModalWindow>({
		isOpen: false
	});
	const [chosenAccount, setChosenAccount] = useState<IAccount | null>(null);

	const closeModal = (): void => setModalState(prevState => ({ ...prevState, isOpen: false }));

	useEffect(() => {
		checkRoles(authUser, CLAIMS.SUPER_ADMIN_AND_PARTNER_ADMIN) && fetchAccountList(dispatch);
	}, []);

	const filteredList = (): IAccount[] => {
		if (searchValue) {
			return accountList?.filter(({ id, name }) => (
				id?.toLowerCase()?.includes(searchValue?.trim()?.toLowerCase())
				|| name?.toLowerCase()?.includes(searchValue?.trim()?.toLowerCase())
			));
		} else {
			return accountList;
		}
	};

	const onEdit = (agency): void => {
		setChosenAccount(agency);
		setIsModalOpen(true);
	};

	const handleSuccess = (): void => {
		setIsModalOpen(false);
		setModalState({
			isOpen: true,
			modalTitle: chosenAccount?.id ? t('ACCOUNT.MODAL_TITLE_EDIT') : t('ACCOUNT.MODAL_TITLE'),
			content: (
				<div className="fs-6">
					{t('ACCOUNT.SUCCESS_MODAL_TEXT')}
				</div>
			),
			confirmButtonText: t('AGENCY.SUCCESS_BTN_NAME'),
			minHeight: 100,
			onConfirm: closeModal,
			onClose: closeModal
		});
	};

	const handleError = (): void => {
		setIsModalOpen(false);
		setModalState({
			isOpen: true,
			modalTitle: chosenAccount?.id ? t('ACCOUNT.MODAL_TITLE_EDIT') : t('ACCOUNT.MODAL_TITLE'),
			content: (
				<div className="fs-6">
					{t('ACCOUNT.FAIL_MODAL_TEXT')}
				</div>
			),
			confirmButtonText: t('AGENCY.FAIL_BTN_NAME'),
			minHeight: 100,
			onConfirm: () => {
				closeModal();
				setIsModalOpen(true);
			},
			onClose: closeModal
		});
	};

	return (
		<>
			<PageTitle breadcrumbs={[]}>{t('MENU.ACCOUNTS')}</PageTitle>

			<div className="card bg-transparent mb-5 mb-xl-8">
				<div className="card-body px-0 py-0 d-sm-flex justify-content-between align-items-center">
					<div className="w-md-400px application-selector application-additional-class">

						<SearchInputSmall
							placeholder="Search accounts"
							searchValue={searchValue}
							setSearchValue={setSearchValue}
						/>

					</div>
					<div className="mt-5 mt-sm-0">
						<ButtonComponent
							buttonName={t('ACCOUNT.BTN_NAME')}
							onButtonClick={(): void => setIsModalOpen(true)}
							additionalClass="w-100 btn-sm w-sm-120px"
						/>
					</div>
				</div>
			</div>

			<div className="card">
				<TableComponent
					column={ACCOUNT_COLUMNS}
					tableData={filteredList()}
					isDataLoading={isAccountListLoading}
					tableLoadingHeight='users-list-table-loading-height'
					getRowComponent={(account): JSX.Element => (
						<AccountTableRow
							{...account}
							key={account?.id}
							onEdit={onEdit}
							isAccountPage
						/>
					)}
				/>
			</div>

			<ModalWindow
				{...modalState}
				onClose={closeModal}
			/>

			<ModalWindow
				isOpen={isModalOpen}
				backdrop="static"
				onClose={(): void => {
					setIsModalOpen(false);
					setChosenAccount(null);
				}}
				modalTitle={chosenAccount?.id ? t('ACCOUNT.MODAL_TITLE_EDIT') : t('ACCOUNT.MODAL_TITLE')}
				minHeight={150}
				content={
					<AddNewAccount
						setChosenAccount={setChosenAccount}
						account={chosenAccount}
						onSuccess={handleSuccess}
						onError={handleError}
					/>
				}

			/>
		</>
	);
};
